// components/common/Sidebar.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  LogOut, 
  Menu,
  X,
  Calendar
} from 'lucide-react';
import { Button } from 'antd';
import { toast } from 'react-hot-toast';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const sidebarItems = [
    { 
      key: 'dashboard',
      path: '/dashboard',
      icon: LayoutDashboard, 
      label: 'Dashboard'
    },
    { 
      key: 'accounts',
      path: '/accounts',
      icon: FileText, 
      label: 'Accounts'
    },
    { 
      key: 'sessions',
      path: '/sessions',
      icon: Calendar, 
      label: 'Sessions'
    },
    { 
      key: 'users',
      path: '/users',
      icon: Users, 
      label: 'Users'
      // Removed adminOnly property
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Menu Button - Only visible on small screens */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          type="primary"
          icon={<Menu className="w-5 h-5" />}
          onClick={() => setSidebarOpen(true)}
          className="shadow-lg"
        />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        /* Mobile: Fixed positioning with transform */
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform 
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        transition-transform duration-300 ease-in-out
        /* Large screens: Static positioning but still in flow */
        lg:relative lg:translate-x-0 lg:flex lg:flex-col lg:w-64 lg:shadow-lg lg:z-auto
      `}>
        
        {/* Logo Section */}
        <div className="flex items-center justify-start p-4 border-b border-gray-200 flex-shrink-0">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">$</span>
          </div>
          <span className="ml-3 text-lg font-bold text-gray-800">
            Divya Enterprise
          </span>
          {/* Close button for mobile only */}
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          {sidebarItems.map((item) => {
            // Removed all role checking - all items visible to all users
            const active = isActive(item.path);
            
            return (
              <button
                key={item.key}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center justify-start px-4 py-3 text-left rounded-lg mb-2 transition-colors ${
                  active 
                    ? 'bg-gray-800 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Profile Section */}
        <div className="border-t border-gray-200 p-4 flex-shrink-0">
          {/* User info */}
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-xl font-medium text-gray-800 truncate">{user?.name || 'User'}</p>
            </div>
          </div>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-start px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 mr-3" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;